<script setup>
import {reactive, ref, watch} from 'vue';
import {message} from 'ant-design-vue';
import {formatDate, getTime} from '@/utils/datetime';
import tourPlayerService from '@/stores/tour-player-register';
import {useAuthStore} from "@/stores/auth";

const authStore = useAuthStore();

const props = defineProps({
  detail: Object,
  categories: Array
});

const player = JSON.parse(localStorage.getItem("player") ?? "{}");
const user = JSON.parse(localStorage.getItem("user") ?? "{}");

const isValid = ref(false);

const form = reactive({
  name: player?.display_name ?? player.last_name,
  email: user.user?.email ?? player.email,
  notes: '',
  category: [],
  policy: true
});

const isDouble = ref(false);

const showFormRegister = () => {
  const login = authStore.user;
  if (!login) {
    message.warning("Vui lòng đăng nhập để đăng ký!");
    return false;
  }

  const dangKy = document.getElementById("dang-ky");
  const btnShowFormRegister = document.querySelector(".btnShowFormRegister");

  if (dangKy && btnShowFormRegister) {
    dangKy.classList.toggle("show");
    btnShowFormRegister.classList.toggle("hide");
  }
};

watch(
    () => props.detail,
    (detailData) => {
      if (detailData) {
        const nowVN = new Date(new Date().toLocaleString("en-US", {timeZone: "Asia/Ho_Chi_Minh"}));

        const reg_start_date = new Date(detailData.reg_start_date);
        const reg_end_date = new Date(detailData.reg_end_date);

        const reg_start_dateVN = new Date(reg_start_date.toLocaleString("en-US", {timeZone: "Asia/Ho_Chi_Minh"}));
        const reg_end_dateVN = new Date(reg_end_date.toLocaleString("en-US", {timeZone: "Asia/Ho_Chi_Minh"}));

        if (reg_end_dateVN >= nowVN) {
          if (reg_start_dateVN <= nowVN) {
            isValid.value = true;
          }
        }
      } else {
        isValid.value = false;
      }
    }
)

const handleCategoryChange = (selectedIds) => {
  const selectedFullCategories = props.categories.filter(cat =>
      selectedIds.includes(cat.documentId)
  );

  const arr = ['XD', 'DM', 'DW'];

  isDouble.value = selectedFullCategories.some(cat => arr.includes(cat.type));
}

const onFinish = async (documentId) => {
  if (!form.policy) {
    message.error("Vui lòng đồng ý với điều khoản");
    return;
  }

  if (!player || !user) {
    message.error("Vui lòng đăng nhập trước khi đăng ký");
    return;
  }

  if (!form.category || form.category.length === 0) {
    message.error("Vui lòng chọn hạng mục trước khi đăng ký");
    return;
  }

  const dataCreate = {
    categories: form.category,
    notes: form.notes,
    tournamentId: documentId,
  };

  const response = await tourPlayerService.send(dataCreate);

  if (response?.data) {
    message.success("Đăng ký thành công!");
    resetForm();
  } else {
    message.error("Đăng ký thất bại. Vui lòng thử lại.");
  }
};

const resetForm = () => {
  form.name = player?.display_name ?? player?.last_name;
  form.email = user.user?.email ?? player.email;
  form.notes = '';
  form.category = [];
  isDouble.value = false;
};
</script>


<template>
  <aside class="col-12 col-sm-12 col-md-12 col-lg-4 theiaStickySidebar">
    <div class="stickysidebar">
      <div class="white-bg book-coach">
        <h4 class="border-bottom">Đăng ký tham gia</h4>
        <p>
          <strong>{{ detail?.name }}</strong>
        </p>
        <div class="dull-bg text-center">
          <div class="d-flex justify-content-between align-items-center">
            <div class="mt-2">
              <p class="mb-1">Ngày bắt đầu</p>
              <h4 class="d-inline-block primary-text mb-0">
                {{ formatDate(detail?.reg_start_date) }}
              </h4>
              <h5 class="">{{ getTime(detail?.reg_start_date) ?? '00.00' }}</h5>
            </div>

            <div class="mt-2">
              <p class="mb-1">Ngày hết hạn</p>
              <h4 class="d-inline-block primary-text mb-0">
                {{ formatDate(detail?.reg_end_date) }}
              </h4>
              <h5 class="">{{ getTime(detail?.reg_end_date) ?? '00.00' }}</h5>
            </div>
          </div>
        </div>
        <div class="d-grid btn-block mt-3">
          <a v-if="isValid"
             class="btn btn-secondary d-inline-flex justify-content-center align-items-center btnShowFormRegister"
             href="javascript:;"
             @click="showFormRegister">
            <i class="feather-calendar"></i>Đăng ký ngay</a>

          <a v-else class="btn btn-primary d-inline-flex justify-content-center align-items-center disabled"
             href="javascript:;">
            <i class="feather-calendar"></i>Đăng ký</a>
        </div>
      </div>
      <div v-if="isValid" id="dang-ky" class="white-bg">
        <h4 class="border-bottom">Đơn đăng ký</h4>
        <a-form
            ref="formRef"
            :model="form"
            layout="vertical"
            @finish="onFinish(detail?.documentId)"
        >
          <a-form-item
              :rules="[{ required: true, message: 'Vui lòng nhập tên!' }]"
              label="Tên"
              name="name"
          >
            <a-input v-model:value="form.name" placeholder="Vui lòng nhập tên" readonly/>
          </a-form-item>

          <a-form-item
              :rules="[
                  { required: true, message: 'Vui lòng nhập email!' },
                  { type: 'email', message: 'Email không hợp lệ!' }
                ]"
              label="Email"
              name="email"
          >
            <a-input v-model:value="form.email" placeholder="Vui lòng nhập email" readonly/>
          </a-form-item>

          <a-form-item :rules="[{ required: true, message: 'Vui lòng chọn hạng mục!' }]" label="Hạng mục thi đấu"
                       name="category">
            <a-select v-model:value="form.category" mode="multiple" placeholder="Chọn hạng mục"
                      @change="handleCategoryChange">
              <a-select-option v-for="item in categories" :key="item.documentId" :value="item.documentId">
                {{ item?.name }}
              </a-select-option>
            </a-select>
          </a-form-item>


          <a-form-item v-if="isDouble" label="Mong muốn của bạn"
                       name="notes">
            <a-textarea v-model:value="form.notes" placeholder="Vui lòng nhập mong muốn của bạn"/>
          </a-form-item>

          <a-form-item :rules="[{ required: true, message: 'Bạn cần đồng ý với điều khoản!' }]" name="policy">
            <div style="display: flex; align-items: flex-start; gap: 10px;">
              <a-checkbox v-model:checked="form.policy"></a-checkbox>
              <span>Hãy chắc rằng bạn đã đọc và đồng ý với điều khoản của giải đấu</span>
            </div>
          </a-form-item>

          <a-form-item>
            <div class="d-grid btn-block">
              <button class="btn btn-secondary d-inline-flex justify-content-center align-items-center"
                      type="submit">
                Gửi yêu cầu<i class="feather-arrow-right-circle ms-1"></i>
              </button>
            </div>
          </a-form-item>
        </a-form>
      </div>
    </div>
  </aside>
</template>

<style scoped>

#dang-ky {
  display: none;
}

#dang-ky.show {
  display: block;
}

.btnShowFormRegister {
  display: block;
}

.btnShowFormRegister.hide {
  display: none !important;
}
</style>